// Client-safe Verified Card Registry contract: types, normalisation, registry
// keys, canonical fingerprint input and buyer-facing copy. No network access
// and no server-only imports, so this module is unit-testable and safe in the
// browser bundle.

import type { CertProvider } from "@/lib/grading/cert-verification";

export const REGISTRY_FINGERPRINT_VERSION = "v1";

export type RegistryStatus = "active" | "provisional" | "revoked" | "under_review" | "merged";

export type RegistryRecord = {
  id: string;
  provider: CertProvider;
  certificate_number: string;
  certificate_number_normalized: string;
  registry_key: string;
  card_fingerprint: string | null;
  fingerprint_version: string;
  verification_status: string;
  verification_method: string | null;
  official_source_url: string | null;
  first_verified_at: string | null;
  last_verified_at: string | null;
  last_rechecked_at: string | null;
  verification_expires_at: string | null;
  card_name: string | null;
  game: string | null;
  year: number | null;
  set_name: string | null;
  set_code: string | null;
  card_number: string | null;
  variant: string | null;
  rarity: string | null;
  language: string | null;
  edition: string | null;
  finish: string | null;
  parallel: string | null;
  serial_number: string | null;
  print_run: number | null;
  grader: string | null;
  grade: string | null;
  grade_numeric: number | null;
  subgrades: Record<string, number | null> | null;
  population_higher: number | null;
  population_same_grade: number | null;
  population_total: number | null;
  official_population_updated_at: string | null;
  official_image_urls: string[];
  registry_confidence: number;
  identity_confidence: number;
  status: RegistryStatus;
  created_at: string;
  updated_at: string;
};

export type RegistryTimelineEvent = {
  id: string;
  event_type: string;
  provider: string;
  status: string;
  method: string | null;
  official_source_url: string | null;
  response_time_ms: number | null;
  cache_hit: boolean;
  summary: string | null;
  verified_at: string | null;
  created_at: string;
};

export type RegistryPopulationSnapshot = {
  id: string;
  provider: string;
  grade: string | null;
  population_same_grade: number | null;
  population_higher: number | null;
  population_total: number | null;
  source_url: string | null;
  captured_at: string;
  confidence: number;
};

export type RegistryMarketStats = {
  sampleSize: number;
  currency: string;
  latestSale: number | null;
  lastSoldAt: string | null;
  averageSale: number | null;
  medianSale: number | null;
  highestSale: number | null;
  lowestSale: number | null;
  change7d: number | null;
  change30d: number | null;
  change90d: number | null;
  change1y: number | null;
  rangeFrom: string | null;
  rangeTo: string | null;
  liquidityScore: number | null;
  volatilityScore: number | null;
  activeListings: number;
  soldListings: number;
  uniqueSellers: number;
  lowestAsk: number | null;
  highestBid: number | null;
  confidence: number;
  source: string;
};

export type RegistryEstimatedValue = {
  available: boolean;
  low: number | null;
  mid: number | null;
  high: number | null;
  confidence: number;
  drivers: string[];
  comparableSales: number;
  dataFreshness: string | null;
  reason?: string;
};

/** Marketplace supply seen on BidNova only — never official grader population. */
export type RegistrySupply = {
  activeListings: number;
  soldListings: number;
  uniqueSellers: number;
  verifiedCopiesSeen: number;
};

// --- normalisation ----------------------------------------------------------

/**
 * Certificate numbers are compared case-insensitively with separators removed,
 * so `ACE 100-000` and `ace100000` resolve to the same canonical record.
 */
export function normalizeCertNumberForRegistry(value: string): string {
  return value.trim().toUpperCase().replace(/[\s\-_/.]/g, "");
}

export function registryKey(provider: CertProvider, certNumber: string): string {
  return `${provider.toUpperCase()}:${normalizeCertNumberForRegistry(certNumber)}`;
}

/** Loose text normalisation used for aliases, search and mismatch comparison. */
export function normalizeText(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\u3000-\u9fff]+/g, " ")
    .trim();
}

export function gradeToNumeric(grade: string | null | undefined): number | null {
  if (!grade) return null;
  const m = /(\d+(?:\.\d+)?)/.exec(grade);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Deterministic fingerprint input. Only stable identity fields — never seller,
 * title, price, timestamps or image URLs. Serialised canonically and hashed
 * server-side so the algorithm can be versioned independently.
 */
export type FingerprintIdentity = {
  game?: string | null;
  year?: number | null;
  setName?: string | null;
  cardNumber?: string | null;
  variant?: string | null;
  language?: string | null;
  edition?: string | null;
  finish?: string | null;
  parallel?: string | null;
  serialNumber?: string | null;
  provider?: string | null;
  grade?: string | null;
};

export function fingerprintInput(identity: FingerprintIdentity): Record<string, string> {
  return {
    v: REGISTRY_FINGERPRINT_VERSION,
    game: normalizeText(identity.game),
    year: identity.year ? String(identity.year) : "",
    set: normalizeText(identity.setName),
    number: normalizeText(identity.cardNumber),
    variant: normalizeText(identity.variant),
    language: normalizeText(identity.language),
    edition: normalizeText(identity.edition),
    finish: normalizeText(identity.finish),
    parallel: normalizeText(identity.parallel),
    serial: normalizeText(identity.serialNumber),
    provider: normalizeText(identity.provider),
    grade: normalizeText(identity.grade),
  };
}

// --- buyer-facing status ----------------------------------------------------

export type RegistryBadgeState =
  | "officially_verified"
  | "verification_pending"
  | "manual_official_check"
  | "not_independently_verified"
  | "verification_revoked"
  | "under_review";

export const REGISTRY_BADGE_COPY: Record<
  RegistryBadgeState,
  { title: string; detail: string; tone: "positive" | "neutral" | "caution" | "danger" }
> = {
  officially_verified: {
    title: "Officially Verified",
    detail: "Matched against the grading company's official certificate record.",
    tone: "positive",
  },
  verification_pending: {
    title: "Verification Pending",
    detail: "We're still confirming this certificate with the grading company.",
    tone: "neutral",
  },
  manual_official_check: {
    title: "Manual Official Check",
    detail: "Check this certificate yourself on the grading company's official page.",
    tone: "caution",
  },
  not_independently_verified: {
    title: "Not Independently Verified",
    detail: "These details were entered by the seller and BidNova has not confirmed them.",
    tone: "caution",
  },
  verification_revoked: {
    title: "Verification Revoked",
    detail: "The grading company's record no longer matches. Treat with caution.",
    tone: "danger",
  },
  under_review: {
    title: "Under Review",
    detail: "BidNova is reviewing this certificate record.",
    tone: "caution",
  },
};

export function registryBadgeState(record: {
  status?: string | null;
  verification_status?: string | null;
} | null): RegistryBadgeState {
  if (!record) return "not_independently_verified";
  if (record.status === "revoked") return "verification_revoked";
  if (record.status === "under_review") return "under_review";
  if (record.status === "provisional") return "not_independently_verified";
  if (record.verification_status === "verified") return "officially_verified";
  if (record.verification_status === "manual_official_link") return "manual_official_check";
  if (
    record.verification_status === "temporarily_unavailable" ||
    record.verification_status === "rate_limited"
  )
    return "verification_pending";
  return "not_independently_verified";
}

export function registryPath(provider: string, certNumber: string): string {
  return `/registry/${provider.toLowerCase()}/${encodeURIComponent(
    normalizeCertNumberForRegistry(certNumber),
  )}`;
}

export function registryUrl(provider: string, certNumber: string, origin?: string): string {
  const base = origin ?? "https://bidnovaauctions.uk";
  return `${base.replace(/\/$/, "")}${registryPath(provider, certNumber)}`;
}

// --- mismatch comparison ----------------------------------------------------

export const REGISTRY_COMPARED_FIELDS = [
  "card_name",
  "set_name",
  "card_number",
  "grade",
  "language",
  "variant",
  "finish",
  "serial_number",
] as const;

export type RegistryMismatch = { field: string; registry: string; listing: string };

/** Compare a seller's listing values against the trusted registry record. */
export function compareToRegistry(
  record: Partial<Record<(typeof REGISTRY_COMPARED_FIELDS)[number], string | null>>,
  listing: Partial<Record<(typeof REGISTRY_COMPARED_FIELDS)[number], string | null>>,
): RegistryMismatch[] {
  const out: RegistryMismatch[] = [];
  for (const field of REGISTRY_COMPARED_FIELDS) {
    const a = normalizeText(record[field]);
    const b = normalizeText(listing[field]);
    if (!a || !b) continue;
    if (a !== b) out.push({ field, registry: record[field] ?? "", listing: listing[field] ?? "" });
  }
  return out;
}
